import {makeAutoObservable, observable} from 'mobx'
import {ArrUtil} from 'yougile-util'
import {DataObjectId, GTDStage} from 'yougile-model'
import {Storage} from '@client/storage'
import {UserSettings} from '@client/user-settings'
import {Backend} from '@common/backend'
import {TabData} from '@components/common/base/tabs'
import {crmStore} from '@components/crm/crm-store'
import {TaskItem} from '@components/my-tasks/task-item'
import {
  getAllTasksFromPrivateLists,
  getAllUsersTasks,
  tabsData
} from '@components/my-tasks/utils'
import {createUseStore} from '@containers/hooks'
import {Task} from '@extensions/task'

export type TabId = 'my' | 'private' | 'by-me' | 'starred' | 'expired'

interface State {
  currentTab: TabId
  allTabs: TabData[]
}

const TasksCurrentTabKey = 'my-tasks-current-tab'

export class MyTasksStore {
  state: State

  constructor() {
    this.state = {
      currentTab:
        (Storage.getItem(TasksCurrentTabKey) as TabId | undefined) ?? 'my',
      allTabs: []
    }

    this.syncTabs()

    makeAutoObservable(
      this,
      {
        state: observable.shallow
      },
      {autoBind: true}
    )
  }

  public syncTabs(): void {
    const tabs = tabsData()

    // check if private tasks tab is available
    const tasksFromPrivateLists = getAllTasksFromPrivateLists()
    const allPrivateTasks = getTasks(getAllUsersTasks('private'), 'current')
    let isPrivateTabVisible = false

    if (tasksFromPrivateLists?.length) {
      for (const task of allPrivateTasks) {
        if (!tasksFromPrivateLists.includes(task.id)) {
          isPrivateTabVisible = true
          break
        }
      }
    }

    const withPrivateTab =
      !Backend.features.myTasksLists ||
      Boolean(
        allPrivateTasks.length &&
          (!tasksFromPrivateLists?.length || isPrivateTabVisible)
      )

    this.state.allTabs = withPrivateTab
      ? tabs
      : tabs.filter(tab => tab.id !== 'private')

    if (this.state.currentTab === 'private' && !withPrivateTab) {
      this.state.currentTab = this.state.allTabs[0].id as TabId
    }
  }

  public setCurrentTab(tab: TabId): void {
    this.state.currentTab = tab
    Storage.setItem(TasksCurrentTabKey, tab)
  }

  // TODO! used only for old tabs. Remove after refactoring!
  getCurrentTasks(
    tab: TabId,
    orderKey?: string
  ): {current: TaskItem[]; done: TaskItem[]} {
    const tasks = getAllUsersTasks(tab)
    const order = orderKey ? UserSettings.getOrder(orderKey) : undefined

    return {
      current: getTasks(tasks, 'current', order),
      done: getTasks(tasks, 'done')
    }
  }
}

// TODO! Deprecated
export const getTasks = (
  tasks: TaskItem[],
  stage: GTDStage,
  order?: DataObjectId[]
): TaskItem[] => {
  const filteredTasks = tasks.filter(t => {
    const task = Task.getById(t.id)
    return (
      task &&
      !crmStore.isDeal(task) &&
      (t.stage === stage || (t.stage !== 'done' && stage !== 'done'))
    )
  })

  if (order?.length) {
    const tasksMap = new Map<DataObjectId, TaskItem>()

    const nonOrdered: TaskItem[] = []
    for (const task of filteredTasks) {
      if (!order.includes(task.id)) {
        nonOrdered.push(task)
      }
      tasksMap.set(task.id, task)
    }

    return [
      ...nonOrdered.sort((a, b) => b.timestamp - a.timestamp),
      ...ArrUtil.defined(
        order
          .filter(itemId => tasksMap.has(itemId))
          .map(orderId => tasksMap.get(orderId))
      )
    ]
  } else {
    return filteredTasks
  }
}

export const [MyTasksContext, useMyTasksStore] = createUseStore(MyTasksStore)
